import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import CustomInput from "../../../../Components/UI/Inputs/CustomInput";
import BoltButton from "../../../../Components/UI/Buttons/BoltButton";
import {
  getClientCards,
  chargeClientCard,
} from "../../../../services/CreditCard/stripe.services";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

const PAYMENT_FEE_PERCENTAGE = 0.03;
const PAYMENT_FEE_LABEL = `${(PAYMENT_FEE_PERCENTAGE * 100).toFixed(
  0
)}% Payment Processing Fee`;

const toCents = (value) => Math.round((parseFloat(value) || 0) * 100);
const fromCents = (cents) => (cents / 100).toFixed(2);

const SkeletonLoader = () => (
  <div className="space-y-4 animate-pulse">
    <div className="bg-ui-surface rounded-lg border border-ui-border p-4">
      <div className="h-6 bg-ui-border rounded w-48 mb-4"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="h-4 bg-ui-border rounded w-24"></div>
          <div className="h-10 bg-ui-border rounded"></div>
        </div>
        <div className="space-y-3">
          <div className="h-4 bg-ui-border rounded w-32"></div>
          <div className="h-10 bg-ui-border rounded"></div>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        <div className="h-4 bg-ui-border rounded w-40"></div>
        <div className="h-10 bg-ui-border rounded"></div>
      </div>
    </div>
  </div>
);

export default function ChargeClientCard({ shipperData, updateShipper }) {
  const { t } = useTranslation("admin");
  const dispatch = useDispatch();
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [chargeAmount, setChargeAmount] = useState("");

  const balance = shipperData?.balance || 0;

  const { displayChargeString, chargeAmountStr } = useMemo(() => {
    const balCents = toCents(chargeAmount);
    const feeCents = Math.round(balCents * PAYMENT_FEE_PERCENTAGE);
    const totalCents = balCents + feeCents;

    return {
      displayChargeString: `${fromCents(balCents)} + ${fromCents(
        feeCents
      )} (${PAYMENT_FEE_LABEL}) = ${fromCents(totalCents)}`,
      chargeAmountStr: fromCents(balCents),
    };
  }, [chargeAmount]);

  const fetchCards = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getClientCards(shipperData?.id);
      setCards(response?.data?.result?.payment_methods || []);
    } catch (error) {
      dispatch(
        showSnackBar({
          text: i18n.t("admin:controls.failedToLoadClientCards"),
          severity: "error",
        })
      );
      console.error("Error fetching cards:", error);
    } finally {
      setIsLoading(false);
    }
  }, [shipperData?.id, dispatch]);

  useEffect(() => {
    if (shipperData) {
      fetchCards();
    }
  }, [fetchCards, shipperData]);

  useEffect(() => {
    const defaultCard = cards.find((c) => c.card?.is_default);
    if (defaultCard) setPaymentMethodId(defaultCard.id);
  }, [cards]);

  const processCharge = async () => {
    if (!chargeAmount || parseFloat(chargeAmount) <= 0) {
      dispatch(
        showSnackBar({
          text: t("controls.pleaseEnterAValidAmount"),
          severity: "warning",
        })
      );
      return;
    }
    if (!paymentMethodId) {
      dispatch(
        showSnackBar({
          text: t("controls.pleaseSelectAPaymentMethod"),
          severity: "warning",
        })
      );
      return;
    }

    setIsProcessing(true);

    try {
      const id = shipperData?.id;
      const payload = {
        payment_method_id: paymentMethodId,
        amount: chargeAmount,
      };

      const response = await chargeClientCard(id, payload);

      if (response.error) {
        throw new Error(
          response.error.response?.data?.message || i18n.t("admin:controls.paymentFailed")
        );
      }

      dispatch(
        showSnackBar({
          text: t("controls.clientBalanceHasBeenUpdated"),
          severity: "success",
        })
      );

      const newBalance = response?.data?.result?.balance;

      // ✅ Update parent user state
      if (updateShipper && typeof newBalance !== "undefined") {
        updateShipper(id, "balance", newBalance);
      }

      setChargeAmount("");
    } catch (error) {
      dispatch(
        showSnackBar({
          text: error.message || t("controls.somethingWentWrongDuringThe"),
          severity: "error",
        })
      );
      console.error("Charge error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!shipperData) return null;

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Balance and Charge Form */}
      <div className="bg-ui-surface rounded-lg border border-ui-border p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* Current Balance */}
          <div className="space-y-1 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-medium text-ui-text">
              {t("controls.clientBalanceCad")}
            </label>
            <CustomInput
              value={`${parseFloat(balance || 0).toFixed(2)} + ${parseFloat(
                chargeAmountStr || 0
              ).toFixed(2)} = ${(
                parseFloat(balance || 0) + parseFloat(chargeAmountStr || 0)
              ).toFixed(2)}`}
              disabled
              className="bg-ui-surface-sunken"
              inputClassName="text-ui-text font-medium text-xs sm:text-sm"
            />
          </div>

          {/* Charge Amount */}
          <div className="space-y-1 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-medium text-ui-text">
              {t("controls.chargeAmountCad")}
            </label>
            <CustomInput
              type="number"
              value={chargeAmount}
              onChange={(e) => setChargeAmount(e.target.value)}
              placeholder={t("controls.enterAmountToCharge")}
              inputClassName="text-ui-text text-xs sm:text-sm"
            />
          </div>
        </div>

        {/* Total Calculation */}
        <div className="mt-3 sm:mt-4 space-y-1 sm:space-y-2">
          <label className="block text-xs sm:text-sm font-medium text-ui-text">
            {t("controls.totalAvailableAmountCad")}
          </label>
          <CustomInput
            value={displayChargeString}
            disabled
            className="bg-ui-surface-sunken"
            inputClassName="text-ui-text font-medium text-xs sm:text-sm"
          />
        </div>
      </div>

      {/* Payment Methods */}
      <div className="bg-ui-surface rounded-lg border border-ui-border p-3 sm:p-4">
        <h3 className="text-xs sm:text-sm font-medium text-ui-text mb-3 sm:mb-4">
          {t("controls.selectPaymentMethod")}
        </h3>

        {cards.length > 0 ? (
          <div className="space-y-2 sm:space-y-3">
            {cards.map((card) => (
              <label
                key={card.id}
                className={`border rounded-lg p-2 sm:p-3 flex items-center cursor-pointer transition-all ${
                  paymentMethodId === card.id
                    ? "border-brand bg-brand-light"
                    : "border-ui-border hover:bg-ui-surface-sunken"
                }`}
              >
                <input
                  type="radio"
                  name="radio_card"
                  checked={paymentMethodId === card.id}
                  onChange={() => setPaymentMethodId(card.id)}
                  className="mr-2 sm:mr-3 text-brand focus:ring-brand"
                />
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                    <div>
                      <p className="text-sm sm:text-base font-semibold capitalize text-ui-text">
                        {t("controls.brandCard", { brand: card.card.brand })}
                      </p>
                      <p className="text-xs sm:text-sm text-ui-text-muted">
                        **** **** **** {card.card.last4}
                      </p>
                    </div>
                    {card.card.is_default && (
                      <span className="text-xs bg-state-success-bg text-green-800 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium self-start sm:self-auto">
                        {t("controls.default")}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8">
            <div className="text-ui-text-subtle mb-2">
              <svg
                className="mx-auto h-8 sm:h-12 w-8 sm:w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
            <p className="text-ui-text-muted text-xs sm:text-sm">
              {t("controls.clientDoesntHaveAnyCard")}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end">
        <BoltButton
          label={isProcessing ? t("controls.processing") : t("controls.chargeNow")}
          variant="primary"
          size="sm"
          onClick={processCharge}
          disabled={isProcessing || !paymentMethodId || !chargeAmount}
          loading={isProcessing}
          className="h-7 sm:h-8 px-3 sm:px-4 text-xs sm:text-sm w-full sm:w-auto"
        />
      </div>
    </div>
  );
}
